<template>
  <FormContainer id="module-scroll-interne">
    <template #title>Informations Module</template>
    <template #content>
      <div class="form__bloc">
        <WrapperToggleSwitch
          id="field_83"
          :model-value="moduleEnCours?.formationRealiseeDansEntreprise"
          @update:model-value="updateFormationRealiseeDansEntreprise"
          label="Formation réalisée dans l’entreprise utilisatrice ?"
          :items="OuiOuNon"
          :required="metaModuleEnCours?.formationRealiseeDansEntreprise?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.formationRealiseeDansEntreprise?.$messageErreurDirty!"
          class="col-12 col-md-18 col-sm-36"
          :readonly
        />
      </div>
      <div class="form__bloc">
        <WrapperDropdown
          id="field_84"
          :model-value="moduleEnCours?.modalitePedagogique"
          @update:model-value="(value: any) => updateModule('modalitePedagogique', activeModuleIndex!, value)"
          label="Modalité pédagogique"
          class="col-12 col-md-18 col-sm-36"
          :items="lov.ModalitePedagogique"
          :required="metaModuleEnCours?.modalitePedagogique?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.modalitePedagogique?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />

        <WrapperInputNumber
          v-if="estDossier && metaModuleEnCours?.dureeFoad?.$status !== PropertyStatus.NotPresent"
          id="field_85"
          :model-value="moduleEnCours?.dureeFoad"
          @update:model-value="(value: any) => updateModule('dureeFoad', activeModuleIndex!, value)"
          label="Durée de la FOAD"
          class="col-12 col-md-18 col-sm-36"
          placeholder="Heures"
          :required="metaModuleEnCours?.dureeFoad?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.dureeFoad?.$messageErreurDirty!"
          :decimal="0"
          :readonly
          deletable
        />
        <WrapperInputNumber
          v-if="estDossier && metaModuleEnCours?.dureeFoad?.$status === PropertyStatus.NotPresent"
          id="field_85"
          label="Durée de la FOAD"
          class="col-12 col-md-18 col-sm-36"
          placeholder="Heures"
          disabled
        />
      </div>
      <div class="form__bloc">
        <WrapperInputNumber
          id="field_86"
          :model-value="moduleEnCours?.duree"
          @update:model-value="(value) => updateDureeFormation(activeModuleIndex!, value as any)"
          :label="moduleEnCours?.typeModule === 'Externe' ? 'Durée de la formation' : 'Durée du bilan'"
          class="col-12 col-md-18 col-sm-36"
          placeholder="Heures"
          :required="metaModuleEnCours?.duree?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.duree?.$messageErreurDirty!"
          :readonly
          deletable
          :decimal="2"
        />
        <WrapperInput
          v-if="metaModuleEnCours?.horairesFormation?.$status !== PropertyStatus.NotPresent"
          id="field_86"
          :model-value="moduleEnCours?.horairesFormation"
          @update:model-value="(value: any) => updateModule('horairesFormation', activeModuleIndex!, value)"
          label="Horaires de la formation"
          class="col-12 col-md-18 col-sm-36"
          placeholder="Texte libre"
          :required="metaModuleEnCours?.horairesFormation?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.horairesFormation?.$messageErreurDirty!"
          :maxlength="50"
          :readonly
          deletable
        />
      </div>
      <div class="form__bloc">
        <WrapperInputDate
          id="field_88"
          :model-value="moduleEnCours?.dateDebut"
          @update:model-value="(value: any) => updateModule('dateDebut', activeModuleIndex!, value)"
          :label="
            moduleEnCours?.typeModule === 'Externe' ? 'Date de début de cycle de formation' : 'Date de début du bilan'
          "
          :hint="HINT_DATE"
          class="col-12 col-md-18 col-sm-36"
          :required="metaModuleEnCours?.dateDebut?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.dateDebut?.$messageErreurDirty!"
          :readonly
        />
        <WrapperInputDate
          id="field_89"
          :model-value="moduleEnCours?.dateFin"
          @update:model-value="(value: any) => updateModule('dateFin', activeModuleIndex!, value)"
          :label="
            moduleEnCours?.typeModule === 'Externe'
              ? 'Date prévue de fin de cycle de formation'
              : 'Date prévue de fin du bilan'
          "
          :hint="HINT_DATE"
          class="col-12 col-md-18 col-sm-36"
          :required="metaModuleEnCours?.dateFin?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.dateFin?.$messageErreurDirty!"
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import type { ModuleDTO } from '@/features/api/extranet/dto/Demande/CXPI'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { DEMANDE_STORE, FormContainer, HINT_DATE } from '@/features/interface/demande/shared'
  import { useDialog } from '@/features/interface/dialog'
  import { OuiOuNon } from '@/features/interface/shared/tools'
  import { noop } from '@/features/shared/tools'
  import { storeToRefs } from 'pinia'
  import { inject } from 'vue'
  import { CXPI_MODULES_ACTIONS } from '../../useModuleActions'

  const store = inject(DEMANDE_STORE)!.getStore<'MODEL_CXPI'>()
  const { setupArrayUpdaterWithKey } = store
  const { moduleEnCours, activeModuleIndex, metaModuleEnCours } = inject(CXPI_MODULES_ACTIONS)!
  const { readonly, lov, estDossier, meta } = storeToRefs(store)

  const dialog = useDialog()

  const updateModule = setupArrayUpdaterWithKey<ModuleDTO>('formation.modules')

  const updateFormationRealiseeDansEntreprise = (value: boolean | null | undefined) => {
    updateModule('formationRealiseeDansEntreprise', activeModuleIndex.value!, value)
    if (value)
      dialog
        .showDialog({
          type: 'Ok',
          title: 'Formation au sein de l’entreprise utilisatrice',
          message:
            'Vous avez sélectionné que la formation se déroule dans l’entreprise utilisatrice, il sera nécessaire de transmettre un programme de formation et la lettre d’engagement des parties.',
          closable: true,
        })
        .catch(noop)
  }

  const updateDureeFormation = async (index: number, value: number | null | undefined) => {
    await updateModule('duree', index!, value)
    if (!meta.value.formation?.dureeTotale?.$estDirty) store.dirtifyWithKey(`formation.dureeTotale`)
  }
</script>
